// =============================================================================
// sequential-add.ts — Ajout MULTIPLE d'entrées quand l'API n'offre qu'un endpoint UNITAIRE.
// =============================================================================
// Décision GATE_HUMAN du 2026-09-21 (arbitrage 1, « sélection multiple par cases à cocher ») appliquée
// aux panneaux de périmètre du détail campagne : l'utilisateur coche n entrées puis valide UNE fois.
//
// POURQUOI CE MODULE EXISTE. Les profils disposent d'un ajout en lot serveur
// (POST /admin/campaigns/:id/profile-scopes/batch) ; les clients et les participations fournisseurs
// n'en ont AUCUN. Le front enchaîne donc n appels unitaires, et c'est précisément là que le risque
// d'échec silencieux apparaît : un `Promise.all` perdrait l'ordre, masquerait les rejets derrière le
// premier d'entre eux, et laisserait l'utilisateur devant un périmètre partiellement écrit sans savoir
// ce qui manque. Trois règles en découlent, encodées ici plutôt que dupliquées dans chaque panneau :
//
//   1. SÉQUENTIEL. Un appel à la fois, dans l'ordre coché. Le serveur voit la même charge qu'un
//      utilisateur qui cliquerait n fois, et l'invalidation de cache du détail campagne ne se
//      déclenche pas n fois en parallèle sur la même clé.
//   2. ON NE S'ARRÊTE PAS AU PREMIER ÉCHEC. Ce qui a été ajouté RESTE ajouté (l'API a déjà écrit), et
//      les entrées suivantes sont tout de même tentées : abandonner la file punirait des entrées
//      valides pour l'erreur d'une autre.
//   3. LE COMPTE RENDU NOMME LES ÉCHECS. Jamais « une erreur est survenue » : le message dit combien
//      d'entrées sont passées et LESQUELLES ont échoué, avec le message de l'API pour chacune.
//
// Ces fonctions sont PURES au sens des effets d'interface : aucun état React, aucun rendu. Elles sont
// donc testables directement, ce qui est la raison de les sortir du composant.

/** Une entrée qui n'a pas pu être ajoutée, avec la raison rendue par l'API. */
export interface SequentialAddFailure {
  readonly id: string;
  readonly message: string;
}

/** Résultat d'un ajout multiple : ce qui est passé, ce qui a échoué. Jamais une exception. */
export interface SequentialAddOutcome {
  readonly added: readonly string[];
  readonly failed: readonly SequentialAddFailure[];
}

/**
 * Seuil au-delà duquel l'interface AVERTIT que l'ajout se fera en autant d'appels (décision
 * GATE_HUMAN : « Au-delà de 25 entrées cochées, affiche un avertissement sobre […]. Ne bloque pas. »).
 * Le seuil est un avertissement, jamais une limite : rien n'est empêché.
 */
export const SEQUENTIAL_ADD_WARN_THRESHOLD = 25;

/** Extrait un message lisible d'une valeur rejetée, quelle qu'en soit la forme. */
function failureMessage(reason: unknown): string {
  if (reason instanceof Error && reason.message !== '') {
    return reason.message;
  }
  if (typeof reason === 'string' && reason !== '') {
    return reason;
  }
  return 'Erreur inconnue';
}

/**
 * Enchaîne `addOne` sur chaque identifiant, dans l'ordre, un appel à la fois.
 *
 * @param ids        Identifiants cochés, dans l'ordre d'affichage.
 * @param addOne     Ajout UNITAIRE (une mutation `mutateAsync`). Son rejet est capturé, jamais propagé.
 * @param onProgress Appelé après CHAQUE tentative, réussie ou non : l'interface montre l'avancement.
 */
export async function runSequentialAdd(
  ids: readonly string[],
  addOne: (id: string) => Promise<unknown>,
  onProgress?: (done: number, total: number) => void,
): Promise<SequentialAddOutcome> {
  const added: string[] = [];
  const failed: SequentialAddFailure[] = [];
  for (const [index, id] of ids.entries()) {
    try {
      // `await` dans une boucle : c'est l'INTENTION (règle 1 ci-dessus), pas une maladresse.
      // Un `Promise.all` serait plus rapide et strictement moins sûr.
      await addOne(id);
      added.push(id);
    } catch (reason: unknown) {
      failed.push({ id, message: failureMessage(reason) });
    }
    onProgress?.(index + 1, ids.length);
  }
  return { added, failed };
}

/** Nom métier de ce qu'on ajoute, pour composer un compte rendu en français correct. */
export interface SequentialAddNoun {
  readonly singular: string;
  readonly plural: string;
  /** Participe passé accordé au singulier, ex. « ajouté » / « ajoutée ». */
  readonly pastParticiple: string;
}

export interface SequentialAddSummary {
  /** `ok` = tout est passé ; `partial` = au moins un ajout ET au moins un échec ; `error` = rien. */
  readonly tone: 'ok' | 'partial' | 'error';
  readonly text: string;
}

/** Texte d'avancement pendant l'enchaînement. Un compte, pas une barre : il est lu par un lecteur d'écran. */
export function sequentialAddProgressText(done: number, total: number): string {
  return `Ajout en cours… ${String(done)} sur ${String(total)}`;
}

/**
 * Compose le compte rendu d'un ajout multiple. Les échecs sont NOMMÉS un par un (identifiant résolu en
 * dénomination par `labelOf` quand c'est possible), avec le message de l'API.
 */
export function sequentialAddSummary(
  outcome: SequentialAddOutcome,
  noun: SequentialAddNoun,
  labelOf: (id: string) => string,
): SequentialAddSummary {
  const okCount = outcome.added.length;
  const okWord = okCount > 1 ? `${noun.pastParticiple}s` : noun.pastParticiple;
  const okNoun = okCount > 1 ? noun.plural : noun.singular;
  const failures = outcome.failed
    .map((failure) => `${labelOf(failure.id)} (${failure.message})`)
    .join(', ');

  if (outcome.failed.length === 0) {
    return { tone: 'ok', text: `${String(okCount)} ${okNoun} ${okWord} au périmètre.` };
  }
  if (okCount === 0) {
    return { tone: 'error', text: `Aucun ajout. Échec : ${failures}.` };
  }
  return {
    tone: 'partial',
    text: `${String(okCount)} ${okNoun} ${okWord} au périmètre. Échec : ${failures}.`,
  };
}

/** Avertissement sobre au-delà du seuil. `null` quand il n'y a rien à signaler. */
export function sequentialAddWarning(count: number, noun: SequentialAddNoun): string | null {
  if (count <= SEQUENTIAL_ADD_WARN_THRESHOLD) {
    return null;
  }
  return `${String(count)} ${noun.plural} cochés : l'ajout se fera en ${String(count)} appels successifs, l'API n'offrant pas d'ajout en lot ici.`;
}
